import { translateLabel } from "@/i18n/messages";
import React, { useState } from "react";
import { PathIcon, PlusIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  TRAIL_PROPOSAL_DIFFICULTIES,
  TRAIL_PROPOSAL_OTHER,
  TRAIL_PROPOSAL_TYPES,
  TRAIL_PROPOSAL_RIDING_STYLES,
  TRAIL_PROPOSAL_SURFACES,
  emptyTrailProposal,
  normalizeTrailProposal,
  submitTrailProposal,
  validateTrailProposal,
} from "@/lib/trailProposal";

const fieldClass = "mt-2 w-full rounded-xl border border-white/10 bg-[#171719] px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none";

function GuidedSelect({ label, value, otherValue, options, onChange, onOtherChange, testId }) {
  const selected = options.find((option) => option.value === value);
  return (
    <label className="block">
      <span className="label-tiny">{label} · optional</span>
      <select
        className={fieldClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>
        ))}
      </select>
      {selected?.help && <p className="mt-1.5 text-[11px] leading-snug text-zinc-500">{selected.help}</p>}
      {value === TRAIL_PROPOSAL_OTHER && (
        <input
          className={fieldClass}
          value={otherValue}
          onChange={(event) => onOtherChange(event.target.value)}
          placeholder={translateLabel("Suggest a new value")}
          aria-label={`Custom ${label.toLowerCase()}`}
          data-testid={`${testId}-other`}
        />
      )}
    </label>
  );
}

export function SuggestTrail({ destinationId, destinationName, compact = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => emptyTrailProposal());
  const [saving, setSaving] = useState(false);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const input = normalizeTrailProposal({ destinationId, form });
    const validation = validateTrailProposal(input);
    if (validation) return toast.error(validation);

    setSaving(true);
    try {
      await submitTrailProposal(input);
      toast.success(translateLabel("Trail proposal submitted"), {
        description: "It will appear only after an admin reviews and approves it.",
      });
      setForm(emptyTrailProposal());
      setOpen(false);
    } catch (error) {
      toast.error(error.message || "Could not submit trail proposal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={compact
            ? "flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-white"
            : "inline-flex items-center gap-1.5 rounded-full border border-[#CCFF00]/25 bg-[#CCFF00]/[0.06] px-3 py-1.5 text-xs font-bold text-[#CCFF00] transition-colors hover:border-[#CCFF00]/45 hover:bg-[#CCFF00]/[0.1]"}
          data-testid="suggest-trail-toggle"
        >
          {compact ? <PathIcon size={14} /> : <PlusIcon size={14} weight="bold" />} Suggest missing trail
        </button>
      </DialogTrigger>

      <DialogContent className="z-[1400] max-h-[90dvh] max-w-2xl overflow-y-auto border-white/10 bg-[#121214] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold uppercase">{translateLabel("Add a missing trail")}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Propose a trail for {destinationName}. Nothing is published until an admin approves it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-5" data-testid="suggest-trail-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Trail name · required")}</span>
              <input className={fieldClass} value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="trail-proposal-name" />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Difficulty")}</span>
              <select className={fieldClass} value={form.difficulty} onChange={(e) => update("difficulty", e.target.value)}>
                {TRAIL_PROPOSAL_DIFFICULTIES.map((option) => <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>)}
              </select>
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label>
              <span className="label-tiny">{translateLabel("Length (m) · optional")}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={form.lengthM} onChange={(e) => update("lengthM", e.target.value)} placeholder="807" />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Elevation gain (m)")}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={form.elevationGainM} onChange={(e) => update("elevationGainM", e.target.value)} />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Elevation loss (m)")}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={form.elevationLossM} onChange={(e) => update("elevationLossM", e.target.value)} />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <GuidedSelect
              label={translateLabel("Trail type")}
              value={form.trailType}
              otherValue={form.trailTypeOther}
              options={TRAIL_PROPOSAL_TYPES}
              onChange={(value) => update("trailType", value)}
              onOtherChange={(value) => update("trailTypeOther", value)}
              testId="trail-proposal-type"
            />
            <GuidedSelect
              label={translateLabel("Riding style")}
              value={form.ridingStyle}
              otherValue={form.ridingStyleOther}
              options={TRAIL_PROPOSAL_RIDING_STYLES}
              onChange={(value) => update("ridingStyle", value)}
              onOtherChange={(value) => update("ridingStyleOther", value)}
              testId="trail-proposal-riding-style"
            />
            <GuidedSelect
              label={translateLabel("Surface")}
              value={form.surface}
              otherValue={form.surfaceOther}
              options={TRAIL_PROPOSAL_SURFACES}
              onChange={(value) => update("surface", value)}
              onOtherChange={(value) => update("surfaceOther", value)}
              testId="trail-proposal-surface"
            />
          </div>

          <label className="block">
            <span className="label-tiny">{translateLabel("Description · optional")}</span>
            <textarea rows={3} className={fieldClass} value={form.description} onChange={(e) => update("description", e.target.value)} />
          </label>

          <label className="block">
            <span className="label-tiny">{translateLabel("Why should this trail be added? · required")}</span>
            <textarea rows={3} className={fieldClass} value={form.reason} onChange={(e) => update("reason", e.target.value)} data-testid="trail-proposal-reason" />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Source URL · optional")}</span>
              <input type="url" className={fieldClass} value={form.sourceUrl} onChange={(e) => update("sourceUrl", e.target.value)} placeholder="https://…" />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Source note · optional")}</span>
              <input className={fieldClass} value={form.sourceNote} onChange={(e) => update("sourceNote", e.target.value)} placeholder={translateLabel("Official trail map, ridden today…")} />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Your name · optional")}</span>
              <input className={fieldClass} value={form.contactName} onChange={(e) => update("contactName", e.target.value)} />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Email · optional")}</span>
              <input type="email" className={fieldClass} value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} />
            </label>
          </div>

          <p className="text-xs text-zinc-500">{translateLabel("Submitting this form never creates a public trail automatically.")}</p>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-50"
            data-testid="trail-proposal-submit"
          >
            {saving ? "Submitting…" : "Submit trail"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
